import classnames from 'classnames';
import { Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MultiDayEventBanner } from 'src/Components/Calendars/MonthCalendar/MultiDayEventBanner';
import { SameDayEvent } from 'src/Components/Calendars/MonthCalendar/SameDayEvent';
import useCalendarDays from 'src/Hooks/CalendarLogic/useCalendarDays';
import useEventNavigation from 'src/Hooks/CalendarLogic/useEventNavigation';
import useWeekDays from 'src/Hooks/CalendarLogic/useWeekDays';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { EventDtoTypeEnum } from 'src/Services/API/generated';
import { getEventVisual } from 'src/Utils/eventVisuals';

import styles from './MonthCalendar.module.scss';
import { CalendarEvent, Props } from './MonthCalendar.types';

export const RIBBON_HEIGHT = 26;
export const CHIP_HEIGHT = 22;
export const EVENT_GAP = 2;
export const EVENT_HEIGHT = CHIP_HEIGHT; // Backwards compat for CalendarTaskItem.

const MAX_CHIPS_PER_CELL = 2;

const SCHOOL_ZONE_LETTER: Partial<Record<EventDtoTypeEnum, 'A' | 'B' | 'C'>> = {
  [EventDtoTypeEnum.SchoolHolidayZoneA]: 'A',
  [EventDtoTypeEnum.SchoolHolidayZoneB]: 'B',
  [EventDtoTypeEnum.SchoolHolidayZoneC]: 'C',
};

const isSameDayEvent = (event: CalendarEvent, day: Dayjs) => {
  const dayStart = day.startOf('day');
  const dayEnd = day.endOf('day');
  if (event.endTime) return event.startTime.isBefore(dayEnd) && event.endTime.isAfter(dayStart);
  return event.startTime.isSame(day, 'day');
};

const MonthCalendar = ({
  selectedDate,
  events,
  onDateClick,
  showPracticalReminders = false,
}: Props) => {
  const weekDays = useWeekDays();
  const days = useCalendarDays(selectedDate);
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const navigateForEvent = useEventNavigation();

  const handleEventClick = useCallback(
    (eventId: string, occurrenceDate?: Dayjs) => {
      const event = events?.find(e => e.id === eventId);
      if (event) navigateForEvent(event, occurrenceDate);
    },
    [events, navigateForEvent]
  );

  const partitioned = useMemo(() => {
    const source = events ?? [];
    const withVisual = source.map(event => ({ event, visual: getEventVisual(event) }));

    const visibleEvents = withVisual.filter(({ visual }) => {
      if (visual.kind === 'reminder' && !showPracticalReminders) return false;
      return true;
    });

    return {
      markers: visibleEvents.filter(({ visual }) => visual.kind === 'marker'),
      ribbons: visibleEvents.filter(({ visual }) => visual.kind === 'ribbon'),
      chips: visibleEvents.filter(
        ({ visual }) => visual.kind === 'chip' || visual.kind === 'reminder'
      ),
    };
  }, [events, showPracticalReminders]);

  const ribbonPositions = useMemo(() => {
    const positions = new Map<string, number>();
    if (partitioned.ribbons.length === 0) return positions;

    const sorted = [...partitioned.ribbons].sort((a, b) => {
      const durationA = a.event.endTime?.diff(a.event.startTime) ?? 24 * 60 * 60 * 1000;
      const durationB = b.event.endTime?.diff(b.event.startTime) ?? 24 * 60 * 60 * 1000;
      if (durationB - durationA !== 0) return durationB - durationA;
      return a.event.startTime.diff(b.event.startTime);
    });

    sorted.forEach(({ event }) => {
      let position = 0;

      while (true) {
        const clash = sorted.some(other => {
          if (other.event === event) return false;
          const otherPos = positions.get(other.event.localId);
          if (otherPos !== position) return false;
          const overlap = days.some(d => {
            return isSameDayEvent(event, d.date) && isSameDayEvent(other.event, d.date);
          });
          return overlap;
        });
        if (!clash) {
          positions.set(event.localId, position);
          break;
        }
        position++;
      }
    });

    return positions;
  }, [partitioned.ribbons, days]);

  const perDay = useMemo(() => {
    const map = new Map<
      string,
      {
        ribbons: typeof partitioned.ribbons;
        chips: typeof partitioned.chips;
        isPublicHoliday: boolean;
        schoolZonesToday: Set<'A' | 'B' | 'C'>;
        schoolZonesYesterday: Set<'A' | 'B' | 'C'>;
        badgeZones: ('A' | 'B' | 'C')[]; // zones that start on this visible day
      }
    >();

    const collectZones = (date: Dayjs) => {
      const zones = new Set<'A' | 'B' | 'C'>();
      partitioned.markers.forEach(({ event, visual }) => {
        if (visual.markerType && SCHOOL_ZONE_LETTER[visual.markerType]) {
          if (isSameDayEvent(event, date)) {
            zones.add(SCHOOL_ZONE_LETTER[visual.markerType] as 'A' | 'B' | 'C');
          }
        }
      });
      return zones;
    };

    days.forEach((day, index) => {
      const key = day.date.format('YYYY-MM-DD');
      const ribbons = partitioned.ribbons.filter(({ event }) => isSameDayEvent(event, day.date));
      const chips = partitioned.chips
        .filter(({ event }) => isSameDayEvent(event, day.date))
        .sort((a, b) => a.event.startTime.diff(b.event.startTime));

      const isPublicHoliday = partitioned.markers.some(
        ({ event, visual }) =>
          visual.markerType === EventDtoTypeEnum.PublicHoliday && isSameDayEvent(event, day.date)
      );

      const schoolZonesToday = collectZones(day.date);
      const schoolZonesYesterday =
        index === 0 ? new Set<'A' | 'B' | 'C'>() : collectZones(days[index - 1].date);

      const badgeZones: ('A' | 'B' | 'C')[] = [];
      (['A', 'B', 'C'] as const).forEach(zone => {
        if (schoolZonesToday.has(zone) && !schoolZonesYesterday.has(zone)) {
          badgeZones.push(zone);
        }
      });

      map.set(key, {
        ribbons,
        chips,
        isPublicHoliday,
        schoolZonesToday,
        schoolZonesYesterday,
        badgeZones,
      });
    });

    return map;
  }, [days, partitioned]);

  const renderCellContent = (day: Dayjs, isFirstDayShown: boolean) => {
    const key = day.format('YYYY-MM-DD');
    const info = perDay.get(key);
    if (!info) return null;

    const ribbonNodes = info.ribbons.map(({ event, visual }) => {
      const position = ribbonPositions.get(event.localId) ?? 0;
      const isStart = day.isSame(event.startTime, 'day');
      const isEnd =
        event.endTime === undefined || event.endTime === null || day.isSame(event.endTime, 'day');
      return (
        <MultiDayEventBanner
          key={event.localId}
          eventId={event.localId}
          isStart={isStart}
          isEnd={isEnd}
          topPx={position * (RIBBON_HEIGHT + EVENT_GAP)}
          isFirstDayShown={isFirstDayShown}
          name={event.name}
          color={visual.color ?? '#9AAB9E'}
          initial={visual.initial}
          description={event.description}
          onClick={() =>
            handleEventClick(event.id, event.isOccurrence ? event.startTime : undefined)
          }
        />
      );
    });

    const ribbonRows =
      info.ribbons.length === 0
        ? 0
        : Math.max(
            ...info.ribbons.map(({ event }) => (ribbonPositions.get(event.localId) ?? 0) + 1)
          );

    const chipStartPx = ribbonRows * (RIBBON_HEIGHT + EVENT_GAP);

    const visibleChips = info.chips.slice(0, MAX_CHIPS_PER_CELL);
    const overflowCount = info.chips.length - visibleChips.length;

    const chipNodes = visibleChips.map(({ event, visual }, chipIndex) => {
      const topPx = chipStartPx + chipIndex * (CHIP_HEIGHT + EVENT_GAP);
      return (
        <SameDayEvent
          key={event.localId}
          topPx={topPx}
          name={event.name}
          description={event.description}
          hour={event.startTime.locale(i18n.language).format('LT')}
          showHour={!isMobile && event.endTime?.isSame(event.startTime, 'day') === true}
          icon={visual.icon ?? 'IconNote'}
          accentColor={visual.color}
          onClick={() =>
            handleEventClick(event.id, event.isOccurrence ? event.startTime : undefined)
          }
        />
      );
    });

    const overflowNode =
      overflowCount > 0 ? (
        <div
          key="more-items"
          className={styles.moreEvents}
          style={{
            top: `${chipStartPx + visibleChips.length * (CHIP_HEIGHT + EVENT_GAP)}px`,
          }}
          onClick={e => {
            e.stopPropagation();
            onDateClick?.(day.toDate());
          }}
        >
          {`+${overflowCount} ${isMobile ? '' : t('calendar.month.items', 'éléments')}`}
        </div>
      ) : null;

    return (
      <>
        {ribbonNodes}
        {chipNodes}
        {overflowNode}
      </>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.calendarHeader}>
        {weekDays.map(day => (
          <div key={day.fullName}>{day.shortName}</div>
        ))}
      </div>

      <div className={styles.calendarGrid}>
        {days.map((day, index) => {
          const key = day.date.format('YYYY-MM-DD');
          const info = perDay.get(key);
          const dayOfWeek = day.date.day(); // 0 = Sunday, 6 = Saturday
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          return (
            <div
              key={key}
              className={classnames(styles.calendarCell, {
                [styles.outOfMonth]: !day.isCurrentMonth,
                [styles.weekend]: isWeekend,
                [styles.clickable]: !!onDateClick,
                [styles.ferie]: info?.isPublicHoliday,
                [styles.vac]: info && info.schoolZonesToday.size > 0,
              })}
              onClick={() => onDateClick?.(day.date.toDate())}
            >
              <div className={styles.cellHeader}>
                <span
                  className={classnames(styles.dayNumber, {
                    [styles.dayToday]: day.isToday,
                    [styles.dayFerie]: info?.isPublicHoliday && !day.isToday,
                  })}
                >
                  {day.dayOfMonth}
                </span>
                {info && info.badgeZones.length > 0 && (
                  <span className={styles.zoneBadge}>{`Vac. ${info.badgeZones.join(' ')}`}</span>
                )}
              </div>
              <div className={styles.eventsContainer}>
                {renderCellContent(day.date, index === 0)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthCalendar;
